import React, { useState } from 'react';
import { FileDown, Mail, Loader2 } from 'lucide-react';
import { Invoice } from '../../types';
import { invoicesApi } from '../../api/client';
import { SendEmailModal } from './SendEmailModal';

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  orange: '#f97316',
  orangeHover: '#ea580c',
  border: '#1e1e2e',
  textSecondary: '#6b6880',
  error: '#ef4444',
  success: '#22c55e',
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface InvoicePdfActionsProps {
  invoice: Invoice;
  compact?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InvoicePdfActions({ invoice, compact = false }: InvoicePdfActionsProps) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    setPdfError(null);
    try {
      await invoicesApi.downloadPdf(invoice.id, invoice.number);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Error al generar el PDF');
      setTimeout(() => setPdfError(null), 4000);
    } finally {
      setPdfLoading(false);
    }
  };

  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: compact ? 5 : 7,
    padding: compact ? '6px 12px' : '8px 16px',
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    cursor: 'pointer',
    fontSize: compact ? 12 : 13,
    fontFamily: 'DM Sans, sans-serif',
    fontWeight: 600,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap' as const,
  };

  const pdfBtnStyle: React.CSSProperties = {
    ...btnBase,
    background: pdfLoading ? '#1a1a2a' : 'linear-gradient(135deg, #f97316, #ea580c)',
    color: pdfLoading ? C.textSecondary : '#fff',
    borderColor: pdfLoading ? C.border : 'transparent',
    boxShadow: pdfLoading ? 'none' : '0 3px 10px rgba(249,115,22,0.3)',
    pointerEvents: pdfLoading ? 'none' : 'auto',
  };

  const emailBtnStyle: React.CSSProperties = {
    ...btnBase,
    background: 'transparent',
    color: C.orange,
    borderColor: 'rgba(249,115,22,0.35)',
  };

  return (
    <>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
        {/* PDF download button */}
        <button
          onClick={handleDownloadPdf}
          style={pdfBtnStyle}
          title="Descargar factura en PDF"
          onMouseEnter={e => {
            if (!pdfLoading) {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 5px 16px rgba(249,115,22,0.45)';
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow = pdfLoading ? 'none' : '0 3px 10px rgba(249,115,22,0.3)';
            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
          }}
        >
          {pdfLoading ? (
            <Loader2 size={compact ? 13 : 14} style={{ animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <FileDown size={compact ? 13 : 14} />
          )}
          {compact ? 'PDF' : pdfLoading ? 'Generando...' : 'Exportar PDF'}
        </button>

        {/* Email send button */}
        <button
          onClick={() => setEmailModalOpen(true)}
          style={emailBtnStyle}
          title="Enviar factura por email"
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(249,115,22,0.1)';
            (e.currentTarget as HTMLButtonElement).style.borderColor = C.orange;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(249,115,22,0.35)';
          }}
        >
          <Mail size={compact ? 13 : 14} />
          {compact ? 'Email' : 'Enviar por Email'}
        </button>

        {/* Error feedback */}
        {pdfError && (
          <span style={{
            fontSize: 12,
            color: C.error,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 6,
            padding: '4px 10px',
          }}>
            {pdfError}
          </span>
        )}
      </div>

      {emailModalOpen && (
        <SendEmailModal
          invoice={invoice}
          onClose={() => setEmailModalOpen(false)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
